import { Copy, Trash2, Zap, MoreVertical, Braces } from 'lucide-react';
import { ThreeDotPopover } from '@dreamtree-org/twreact-ui';

/**
 * Overflow (kebab) menu that collects the low-frequency chat actions — the "Auto"
 * approval toggle, copy-whole-conversation, and clear-chat — so the surrounding row stays
 * a single non-overflowing line instead of a strip of pills that pushed the close
 * affordance off-screen in the 360px dock. Shared by the internal chat and the embed
 * widget, which have the same width constraint and pass `extraItems` for what is unique to
 * them (the embed's mute toggle, for instance).
 */
export default function ChatActionsMenu({
  approvalMode, onToggleApprovalMode, onCopyConversation, onCopyConversationJson,
  onClearChat, hasMessages, extraItems = [],
}) {
  const items = [];
  if (onToggleApprovalMode) {
    const on = approvalMode === 'auto';
    items.push({
      key: 'auto',
      label: `Auto-approve · ${on ? 'On' : 'Off'}`,
      icon: Zap,
      onClick: () => onToggleApprovalMode(on ? 'attended' : 'auto'),
    });
  }
  items.push(...extraItems);
  if (hasMessages && onCopyConversation) {
    items.push({ key: 'copy', label: 'Copy conversation', icon: Copy, onClick: onCopyConversation });
  }
  if (hasMessages && onCopyConversationJson) {
    items.push({ key: 'copy-json', label: 'Copy C.JSON', icon: Braces, onClick: onCopyConversationJson });
  }
  if (hasMessages && onClearChat) {
    items.push({
      key: 'clear',
      label: 'Clear chat',
      icon: Trash2,
      destructive: true,
      onClick: () => {
        if (window.confirm('Clear this chat? You won\'t be able to see this conversation again from here.')) onClearChat();
      },
    });
  }
  if (items.length === 0) return null;
  return (
    <ThreeDotPopover
      items={items}
      ariaLabel="Chat actions"
      // A DROP-UP. The trigger is the leftmost control in a composer pinned to the bottom of
      // a fixed-height, overflow-hidden panel, so the library's default (absolute with
      // top:auto — it falls below the trigger) is clipped away entirely. `bottom-full` opens
      // upward into the thread instead, which is inside the panel. `left-0 right-auto` is the
      // matching horizontal fix: right-anchoring belongs to a trigger at the RIGHT edge, and
      // here it pushed the menu off the panel's left side. menuClass is twMerge'd last, so
      // these win over the defaults; `mt-0` just drops the then-dead default `mt-2`.
      menuClass="bottom-full mt-0 mb-2 left-0 right-auto min-w-[200px] max-w-[240px]"
      menuItemClass="whitespace-nowrap"
      trigger={(
        <button
          type="button"
          aria-label="Chat actions"
          title="More actions"
          className="p-2 rounded-full text-secondary-500 hover:bg-[var(--app-border)]/40 shrink-0 transition-colors"
        >
          <MoreVertical size={18} />
        </button>
      )}
    />
  );
}
